import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import Loader from 'components/molecules/Loader';
import AppBreadcrumb from 'components/molecules/common/AppBreadcrumb';
import { KpiCardsContainer, type KpiCardsExportHandle } from '../PPAHomePage';
import PerformanceByLensSection from './PerformanceByLensSection';
import OverallPerformanceSection from './OverallPerformanceSection';
import usePerformanceSummaryMeta from './hooks/usePerformanceSummaryMeta';
import type { PerformanceByLensExportHandle } from './PerformanceByLensSection';
import type { OverallPerformanceExportHandle } from './OverallPerformanceSection';
import { type PerformanceSummaryDrilldownFilter } from './hooks/usePerformanceSummaryKpis';
import PerformanceSummaryExportModal, { type ExportOption } from './PerformanceSummaryExportModal';
import {
    buildPerformanceSummaryKpiExportRows,
    buildPerformanceSummarySectionExportRows,
    downloadExcel,
    ExportKpiItem,
    getPerformanceSummarySectionTableData
} from 'utils/ppaHelpers';
import DownloadIcon from 'assets/svg/DownloadIcon';
import {
    PPA_PERF_SUMMARY_BACK_BTN,
    PPA_PERF_SUMMARY_EXPORT_BTN,
    PPA_PERF_SUMMARY_INFO_BTN,
    PPA_PERF_SUMMARY_KPI_SECTION,
    PPA_PERF_SUMMARY_LENS_SECTION,
    PPA_PERF_SUMMARY_LOADING,
    PPA_PERF_SUMMARY_OVERALL_SECTION,
    PPA_PERF_SUMMARY_PAGE,
    PPA_PERF_SUMMARY_CONTENT,
    PPA_PERF_SUMMARY_SPLIT_RESIZER,
    PPA_PERF_SUMMARY_TEST_ID_PREFIX,
    PPA_PERF_SUMMARY_TITLE,
} from 'constants/ppaTestIds';

const RESIZER_WIDTH_PX = 8;
const MIN_LEFT_PANE_PX = 280;
const MIN_RIGHT_PANE_PX = 280;

function clampLeftPaneWidth(containerWidthPx: number, requestedLeftPx: number): number {
    const inner = Math.max(0, containerWidthPx - RESIZER_WIDTH_PX);
    if (inner < 1) return 0;
    const maxLeft = inner - MIN_RIGHT_PANE_PX;
    if (maxLeft < MIN_LEFT_PANE_PX) return Math.floor(inner * 0.5);
    return Math.max(MIN_LEFT_PANE_PX, Math.min(maxLeft, requestedLeftPx));
}

const PageHeaderSection = ({ title, onBack, onExportClick }: { title: string; onBack: () => void; onExportClick: () => void }) => (
    <div className="flex justify-between items-center w-full pb-3">
        <div className="flex items-center gap-2">
            <button
                type="button"
                onClick={onBack}
                className="flex h-8 w-8 items-center justify-center rounded hover:bg-backgroundGreyAlt transition-colors"
                aria-label="Go back"
                data-testid={PPA_PERF_SUMMARY_BACK_BTN}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M10.7071 5.29289C11.0976 5.68342 11.0976 6.31658 10.7071 6.70711L6.41421 11H20C20.5523 11 21 11.4477 21 12C21 12.5523 20.5523 13 20 13H6.41421L10.7071 17.2929C11.0976 17.6834 11.0976 18.3166 10.7071 18.7071C10.3166 19.0976 9.68342 19.0976 9.29289 18.7071L3.29289 12.7071C2.90237 12.3166 2.90237 11.6834 3.29289 11.2929L9.29289 5.29289C9.68342 4.90237 10.3166 4.90237 10.7071 5.29289Z" fill="#161227"/>
                </svg>
            </button>
            <h1
                className="m-0 text-[20px] font-semibold leading-8 tracking-[0.5px] text-fontColor"
                data-testid={PPA_PERF_SUMMARY_TITLE}
            >
                {title}
            </h1>
            <Tooltip
                placement="right"
                color="#000000e0"
                overlayInnerStyle={{ width: 720 }}
                title={(
                    <div>
                        <p className="m-0">
                            A consolidated view of current performance and historical PPA actions for the selected scope and
                            time period.
                        </p>
                        <ul className="mb-0 mt-1 list-disc pl-5">
                            <li>Top tiles highlight key performance metrics and year-over-year change.</li>
                            <li>
                                Overall Performance shows performance across key business dimensions (e.g., Occasion, Channel).
                            </li>
                            <li>
                                Performance by Lens tracks historical PPA actions (Activate, Migrate, Boost, Remove).
                            </li>
                        </ul>
                    </div>
                )}
            >
                <InfoCircleOutlined className="cursor-pointer opacity-50" data-testid={PPA_PERF_SUMMARY_INFO_BTN} />
            </Tooltip>
        </div>
        <Button
            type="default"
            onClick={onExportClick}
            className="!h-8 !w-8 !min-w-8 !p-0 !inline-flex !items-center !justify-center !rounded-[6px] !border-[#d9d9d9] !shadow-[0px_2px_0px_rgba(0,0,0,0.02)] hover:!border-[#d9d9d9]"
            aria-label="Download"
            data-testid={PPA_PERF_SUMMARY_EXPORT_BTN}
        >
            <DownloadIcon />
        </Button>
    </div>
);

const PerformanceSummary = () => {
    const navigate = useNavigate();
    const [drilldownFilters, setDrilldownFilters] = useState<PerformanceSummaryDrilldownFilter[]>([]);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [selectedExportOption, setSelectedExportOption] = useState<ExportOption | null>(null);

    const kpiCardsRef = useRef<KpiCardsExportHandle>(null);
    const overallPerformanceRef = useRef<OverallPerformanceExportHandle>(null);
    const performanceByLensRef = useRef<PerformanceByLensExportHandle>(null);

    const splitRowRef = useRef<HTMLDivElement>(null);
    const [leftPaneWidthPx, setLeftPaneWidthPx] = useState(500);
    const paneResizeDragRef = useRef<{ startClientX: number; startLeftPx: number } | null>(null);

    const clampLeftToContainer = useCallback(() => {
        const el = splitRowRef.current;
        if (!el) return;
        const cw = el.clientWidth;
        if (cw <= 0) return;
        setLeftPaneWidthPx((prev) => clampLeftPaneWidth(cw, prev));
    }, []);

    useLayoutEffect(() => {
        clampLeftToContainer();
    }, [clampLeftToContainer]);

    useEffect(() => {
        const el = splitRowRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(() => clampLeftToContainer());
        ro.observe(el);
        return () => ro.disconnect();
    }, [clampLeftToContainer]);

    const onPaneResizePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        e.preventDefault();
        paneResizeDragRef.current = { startClientX: e.clientX, startLeftPx: leftPaneWidthPx };
        e.currentTarget.setPointerCapture(e.pointerId);
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
    };

    const onPaneResizePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!paneResizeDragRef.current || !splitRowRef.current) return;
        const cw = splitRowRef.current.clientWidth;
        const delta = e.clientX - paneResizeDragRef.current.startClientX;
        const next = clampLeftPaneWidth(cw, paneResizeDragRef.current.startLeftPx + delta);
        setLeftPaneWidthPx(next);
    };

    const endPaneResizePointer = (e: React.PointerEvent<HTMLDivElement>) => {
        if (paneResizeDragRef.current) {
            paneResizeDragRef.current = null;
            try {
                e.currentTarget.releasePointerCapture(e.pointerId);
            } catch {
                /* already released */
            }
        }
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
    };

    const { performanceSummaryMeta, isLoading: isMetaLoading, isPending: isMetaPending } = usePerformanceSummaryMeta();
    const metaFeatures = performanceSummaryMeta?.features ?? [];
    const title = performanceSummaryMeta?.title ?? '';
    const kpiConfig = metaFeatures?.find((item: any) => item?.feature_id === "kpi_tiles")?.tiles || [];
    const overallPerformanceFeature = metaFeatures.find((feature) => feature.feature_id === 'over_all_performance')
    const performanceByLensFeature = metaFeatures.find((feature) => feature.feature_id === 'performance_by_lens')

    const handleBack = () => navigate(-1);

    const handleExport = async () => {
        if (!selectedExportOption) return;

        const fileName = `${selectedExportOption}_${new Date().toISOString().slice(0, 10)}`;
        const rows: Array<Array<string | number>> = [];

        if (selectedExportOption === 'kpis') {
            const kpiData = kpiCardsRef.current?.getExportData() ?? [];
            rows.push(...buildPerformanceSummaryKpiExportRows(kpiData as ExportKpiItem[]));
        }
        else rows.push(...buildPerformanceSummarySectionExportRows(getPerformanceSummarySectionTableData({
            selectedExportOption,
            getOverallPerformanceData: () => overallPerformanceRef.current?.getExportData(),
            getPerformanceByLensData: () => performanceByLensRef.current?.getExportData(),
        })));

        await downloadExcel(fileName, rows, { moduleKey: 'performance_summary' });
        setIsExportModalOpen(false);
    };

    if (isMetaLoading || isMetaPending) {
        return (
            <div
                className="flex w-full bg-white"
                style={{ minHeight: 'calc(100vh - 100px)' }}
                data-testid={PPA_PERF_SUMMARY_LOADING}
            >
                <Loader size="default" />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2 py-2" data-testid={PPA_PERF_SUMMARY_PAGE}>
            <div className="flex flex-col gap-2">
                <AppBreadcrumb items={[{ label: 'Opportunity Assessment' }, { label: title }]} />
            </div>
            <div className="bg-white p-4 rounded-lg" data-testid={PPA_PERF_SUMMARY_CONTENT}>
                <div className="flex flex-col gap-4">
                    <PageHeaderSection title={title} onBack={handleBack} onExportClick={() => setIsExportModalOpen(true)} />
                </div>

                <div className="flex flex-wrap gap-4 pb-4" data-testid={PPA_PERF_SUMMARY_KPI_SECTION}>
                    <KpiCardsContainer
                        ref={kpiCardsRef}
                        config={kpiConfig}
                        testIdPrefix={PPA_PERF_SUMMARY_TEST_ID_PREFIX}
                    />
                </div>

                <div ref={splitRowRef} className="flex w-full min-w-0 flex-row items-stretch gap-0">
                    <div
                        className="min-w-0 shrink-0 overflow-hidden"
                        style={{ width: leftPaneWidthPx }}
                        data-testid={PPA_PERF_SUMMARY_OVERALL_SECTION}
                    >
                        <OverallPerformanceSection
                            ref={overallPerformanceRef}
                            metaFeature={overallPerformanceFeature}
                            onDrilldownFiltersChange={setDrilldownFilters}
                        />
                    </div>
                    <div
                        role="separator"
                        aria-orientation="vertical"
                        aria-label="Resize Overall Performance and Performance by Lens panels"
                        className="flex w-2 shrink-0 cursor-col-resize touch-none select-none flex-col items-center self-stretch py-2 hover:bg-black/[0.04]"
                        data-testid={PPA_PERF_SUMMARY_SPLIT_RESIZER}
                        onPointerDown={onPaneResizePointerDown}
                        onPointerMove={onPaneResizePointerMove}
                        onPointerUp={endPaneResizePointer}
                        onPointerCancel={endPaneResizePointer}
                    >
                        <span className="w-px flex-1 bg-borderGrey" aria-hidden />
                    </div>
                    <div className="min-w-0 flex-1 overflow-hidden" data-testid={PPA_PERF_SUMMARY_LENS_SECTION}>
                        <PerformanceByLensSection
                            ref={performanceByLensRef}
                            metaFeature={performanceByLensFeature}
                            drilldownFilters={drilldownFilters}
                        />
                    </div>
                </div>
            </div>
            <PerformanceSummaryExportModal
                open={isExportModalOpen}
                selectedExportOption={selectedExportOption}
                onClose={() => setIsExportModalOpen(false)}
                onOptionChange={setSelectedExportOption}
                onExport={handleExport}
            />
        </div>
    );
};

export default PerformanceSummary;
